<template>
  <div
    v-if="cappedColumns.length > 0"
    :class="[
      'dashboard-row-grid',
      `dashboard-row-grid--cols-${gridColCount}`,
      { 'dashboard-row-grid--no-same-height': !isSameHeight },
    ]"
  >
    <div v-for="column in cappedColumns" :key="columnKey(column)" class="dashboard-column">
      <Billboard
        v-if="column.DashboardWidgetType?.type === 'billboard'"
        :config="column.config"
        :data="column.data"
        :preview-mode="previewMode"
      />
      <Content
        v-else-if="column.DashboardWidgetType?.type === 'content'"
        :config="column.config"
        :data="column.data"
        :preview-mode="previewMode"
      />
      <Claim
        v-else-if="column.DashboardWidgetType?.type === 'claims'"
        :config="column.config"
        :data="column.data"
        :widget-data="column.data"
        :program-data="row.Program"
        :is-mobile="isMobile"
        :preview-mode="previewMode"
      />
      <Headline
        v-else-if="column.DashboardWidgetType?.type === 'headline'"
        :config="column.config"
        :data="column.data"
        :preview-mode="previewMode"
      />
      <Progress
        v-else-if="column.DashboardWidgetType?.type === 'progress'"
        :config="column.config"
        :data="column.data"
        :widget-data="column.data"
        :is-mobile="isMobile"
        :preview-mode="previewMode"
      />
      <Wallet
        v-else-if="column.DashboardWidgetType?.type === 'wallet-balance'"
        :config="column.config"
        :data="column.data"
        :is-mobile="isMobile"
        :preview-mode="previewMode"
      />
      <AggregateLearningLeaderboardMinimizedView
        v-else-if="column.DashboardWidgetType?.type === 'aggregate-learning-leaderboard'"
        :config="column.config"
        :preview-mode="previewMode"
      />
      <Leaderboard
        v-else-if="column.DashboardWidgetType?.type === 'leaderboard'"
        :config="column.config"
        :program-id="leaderboardProgramId"
        mode="dashboard"
        :preview-mode="previewMode"
      />
      <MetricsSummaryBar
        v-else-if="column.DashboardWidgetType?.type === 'metrics-summary'"
        :metrics="metrics"
        :metrics-config="column.config && column.config.metrics ? column.config.metrics : null"
        :loading="metricsLoading"
        :preview-mode="previewMode"
      />
    </div>
  </div>
</template>

<script>
import Billboard from "./Billboard.vue";
import Content from "./Content.vue";
import Claim from "./Claim.vue";
import Headline from "./Headline.vue";
import Progress from "./Progress.vue";
import Wallet from "./Wallet.vue";
import AggregateLearningLeaderboardMinimizedView from "@/components/aggregate-learning-leaderboard/AggregateLearningLeaderboardMinimizedView.vue";
import Leaderboard from "../Leaderboard.vue";
import MetricsSummaryBar from "@/components/participant/programs/MetricsSummaryBar.vue";
import { compareNullableNumbers } from "@/utils/dashboardOrder";

const RENDERABLE_WIDGET_TYPES = new Set([
  "billboard",
  "headline",
  "content",
  "wallet-balance",
  "claims",
  "progress",
  "leaderboard",
  "aggregate-learning-leaderboard",
  "metrics-summary",
]);

export function isRenderableDashboardColumn(column) {
  return RENDERABLE_WIDGET_TYPES.has(column?.DashboardWidgetType?.type);
}

export default {
  name: "DashboardRowRenderer",
  components: {
    Billboard,
    Content,
    Claim,
    Headline,
    Progress,
    Wallet,
    AggregateLearningLeaderboardMinimizedView,
    Leaderboard,
    MetricsSummaryBar,
  },
  props: {
    row: {
      type: Object,
      required: true,
    },
    previewMode: {
      type: Boolean,
      default: false,
    },
    metrics: {
      type: Object,
      default: null,
    },
    metricsLoading: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    columnKey(column) {
      return column.columnId != null ? column.columnId : column.dashboardColumnId;
    },
    compareColumns(a, b) {
      const positionComparison = compareNullableNumbers(a.position, b.position);
      if (positionComparison !== 0) return positionComparison;

      const dateComparison = compareNullableNumbers(
        a.createDate ? new Date(a.createDate).getTime() : null,
        b.createDate ? new Date(b.createDate).getTime() : null
      );
      if (dateComparison !== 0) return dateComparison;

      return String(this.columnKey(a) ?? "").localeCompare(
        String(this.columnKey(b) ?? ""),
        undefined,
        { numeric: true }
      );
    },
  },
  computed: {
    leaderboardProgramId() {
      return this.row.programId == null ? this.row.programId : String(this.row.programId);
    },
    maxColumns() {
      const configuredMax = Number(this.row?.DashboardRowType?.maxColumns);
      if (Number.isInteger(configuredMax) && configuredMax >= 1 && configuredMax <= 3) {
        return configuredMax;
      }
      return this.row?.DashboardRowType?.type === "triple" ? 3 : 1;
    },
    cappedColumns() {
      const columns = (this.row?.DashboardColumns ?? []).filter(isRenderableDashboardColumn);
      const sorted = [...columns].sort(this.compareColumns);
      return sorted.slice(0, this.maxColumns);
    },
    gridColCount() {
      return this.maxColumns;
    },
    isMobile() {
      const breakpoints = [
        this.$vuetify?.breakpoint,
        this.$vuetify?.framework?.breakpoint,
        this.$root?.$options?.vuetify?.breakpoint,
        this.$root?.$options?.vuetify?.framework?.breakpoint,
      ].filter(Boolean);
      return breakpoints.some(
        (breakpoint) =>
          breakpoint.smAndDown === true ||
          breakpoint.xs ||
          breakpoint.sm ||
          (Number.isFinite(breakpoint.width) && breakpoint.width < 960)
      );
    },
    isSameHeight() {
      const column = this.cappedColumns.find(
        ({ config }) => typeof config?.rowMeta?.sameHeight === "boolean"
      );
      return column?.config.rowMeta.sameHeight ?? true;
    },
  },
};
</script>

<style scoped>
.dashboard-row-grid {
  display: flex;
  flex-direction: row;
  gap: 24px;
  width: 100%;
}
.global-rows-section .dashboard-row-grid {
  padding: 0 32px 24px;
}

.dashboard-column {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.dashboard-column > * {
  flex: 1;
}

.dashboard-row-grid--cols-1 .dashboard-column {
  flex: 1;
}

.dashboard-row-grid--cols-2 .dashboard-column {
  flex: 1;
}

.dashboard-row-grid--cols-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
}

.dashboard-row-grid--cols-3 .dashboard-column {
  min-width: 0;
}

.dashboard-row-grid--no-same-height {
  align-items: start;
}

.program-group .dashboard-row-grid {
  padding: 0 16px 24px;
}

@media (max-width: 959px) {
  .dashboard-row-grid {
    flex-direction: column;
  }

  .program-group .dashboard-row-grid {
    padding: 0 12px 24px;
  }

  .dashboard-row-grid--cols-3 {
    display: flex;
    flex-direction: column;
  }

  .dashboard-column {
    width: 100%;
  }
}

.widget-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80px;
  border: 2px dashed #666;
  border-radius: 8px;
  background: #1a1a2e;
  color: #aaa;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
}
</style>
